/**
 * CodeReviewGraphPanel - per-tenant switch for the agent's code review graph
 */

import type { UpdateInternalTenantRequest } from '@resolve-ai/shared';

interface CodeReviewGraphPanelProps {
  enabled: boolean;
  /** The tenant update last submitted; its value is shown only while saving */
  pendingUpdate?: UpdateInternalTenantRequest;
  isSaving: boolean;
  onChange: (enabled: boolean) => void;
}

export function CodeReviewGraphPanel({
  enabled,
  pendingUpdate,
  isSaving,
  onChange,
}: CodeReviewGraphPanelProps) {
  const pendingValue = pendingUpdate?.codeReviewGraphEnabled;
  // While saving, show the value being saved instead of the stale server
  // value. React Query keeps the last submitted value after a failed save,
  // so outside a save the server value wins.
  const shownValue = isSaving && pendingValue !== undefined ? pendingValue : enabled;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Agent Features</h3>
      <div className="flex items-start justify-between gap-6">
        <div>
          <p className="font-medium text-gray-900 dark:text-white">Code review graph</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Indexes each cloned repository so the agent can look up symbols, callers and the impact
            of a change instead of reading whole files. Turn it off to run the agent without the
            graph. Applies to new tasks.
          </p>
        </div>
        <label className="inline-flex items-center cursor-pointer shrink-0">
          <input
            type="checkbox"
            checked={shownValue}
            disabled={isSaving}
            onChange={(e) => onChange(e.target.checked)}
            aria-label="Code review graph"
            className="sr-only peer"
          />
          <div className="relative w-9 h-5 bg-gray-200 dark:bg-gray-600 rounded-full transition-colors peer-checked:bg-indigo-600 peer-disabled:opacity-50 peer-focus-visible:ring-2 peer-focus-visible:ring-indigo-500 after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-4" />
        </label>
      </div>
    </div>
  );
}
